import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {ProductTile as ProductTileFeature} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile.types';
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  RecoListTrackingData,
  useTrackFeatureInViewImpression,
  useTrackWishlist,
} from '@aboutyou/tracking';
import {
  Button,
  LoadingSpinner,
  Z_INDEXES,
  mobileOnly,
  useShowToast,
} from '@aboutyou/ui-components';
import React, {useState} from 'react';
import styled, {css} from 'styled-components';
import {MarkOptional} from 'ts-essentials';
import {ProductTileDeprecated} from '../../../features/ProductTile/ProductTileDeprecated';
import {ProductTileDeprecatedTracked} from '../../../features/ProductTile/ProductTileDeprecatedTracked';
import {ShallowRenderer} from '../../../features/ProductTile/ShallowRender';
import {ShallowRenderingState} from '../../../features/ProductTile/utils';
import {RemoveItemButton} from './RemoveItemButton';
import {RemoveItemConfirmModal} from './RemoveItemConfirmModal';

export interface WishlistProductTileProps {
  gridColumns?: number;
  tile: ProductTileFeature;
  importance?: 'high' | 'low' | 'auto';
  itemsPreferences?: ProductTilePreferences;
  streamPosition: number;
  trackingData?: MarkOptional<RecoListTrackingData, 'sectionIndex'> & {
    sortingChannel?: string;
  };
  onClick?: (ev: React.MouseEvent) => void;
  shallowRenderingState?: ShallowRenderingState;
  showConfirmOnDeleteItem?: boolean;
  onClickAddToBasket: ({
    productId,
    product,
    brandName,
    couponPromotionId,
  }: {
    productId: number;
    product?: Product;
    brandName: string;
    couponPromotionId?: string;
  }) => void;
  smallAddToBasketCtaText: boolean;
  onSoldoutCtaClick: (id: number) => void;
  onSimilarProductsClick: (product: Product) => void;
  interactiveHoverMode?: boolean;
  onColorHover?: (colorProductId: number) => void;
  onColorLeave?: VoidFunction;
  productTileGalleryMode?: 'hover' | 'always';
  soldOutColorProductId?: number;
  enableImageGalleryAnimation?: boolean;
  openInNewTab?: boolean;
}

export const WishlistProductTile = ({
  tile,
  streamPosition,
  trackingData,
  shallowRenderingState,
  gridColumns,
  importance,
  itemsPreferences,
  onClick,
  showConfirmOnDeleteItem,
  smallAddToBasketCtaText,
  onClickAddToBasket,
  onSoldoutCtaClick,
  onSimilarProductsClick,
  interactiveHoverMode,
  onColorHover,
  onColorLeave,
  productTileGalleryMode,
  soldOutColorProductId,
  enableImageGalleryAnimation,
  openInNewTab,
}: WishlistProductTileProps) => {
  const tracker = useTrackWishlist({
    product: tile.product,
    streamPosition,
    brand: tile.brand,
  });

  const showToast = useShowToast();
  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const handleClickRemoveItemButton = () => setIsSpinnerVisible(true);
  const handleClickRemoveItemSuccess = () => {
    tracker('removed-from-wishlist');
  };

  const handleClickRemoveItemError = () => {
    setIsSpinnerVisible(false);
    setIsConfirmModalVisible(false);

    showToast({
      content: t('Oops! Something went wrong!'),
      type: 'ERROR',
      'data-testid': 'RemoveFromWishlistButtonToast',
    });
  };

  const {t} = useTranslation();

  const {ref: trackFeatureRef} = useTrackFeatureInViewImpression({
    label: 'WL_similar_products',
  });

  const SoldOutButtonWrapper = interactiveHoverMode
    ? SoldOutWrapper
    : ButtonWrapper;

  return (
    <>
      <ProductTileDeprecatedTracked
        tile={tile}
        streamPosition={streamPosition}
        trackingData={trackingData}
      >
        <ShallowRenderer
          shallowRenderingState={shallowRenderingState}
          id={`productTile-${tile.product.id}`}
          renderer={({
            ref,
            isInViewOnRemount,
          }: {
            ref: React.RefObject<HTMLElement | null>;
            isInViewOnRemount: boolean;
          }) => {
            return (
              <>
                <ProductTileDeprecated
                  enableImageGalleryAnimation={enableImageGalleryAnimation}
                  gridColumns={gridColumns}
                  productTile={tile}
                  importance={isInViewOnRemount ? 'high' : importance}
                  itemsPreferences={itemsPreferences}
                  onClick={onClick}
                  showColorVariantsSoldOut={!interactiveHoverMode}
                  showLowestPriorPriceHint={true}
                  addCtaVisibility={interactiveHoverMode ? 'hover' : 'always'}
                  interactiveHoverMode={interactiveHoverMode}
                  productTileGalleryMode={productTileGalleryMode}
                  soldOutColorProductId={soldOutColorProductId}
                  selectedCoupon={tile.couponPromotionId}
                  onColorHover={onColorHover}
                  onColorLeave={onColorLeave}
                  openInNewTab={openInNewTab}
                  onColorClick={colorProductId => {
                    onClickAddToBasket({
                      productId: colorProductId,
                      brandName: tile.brand.name,
                    });
                  }}
                  cta={
                    <ButtonWrapper>
                      <StyledAddButton
                        data-testid="ProductTileAddToBasketButton"
                        aria-label={t('Add to basket')}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onClickAddToBasket({
                            productId: tile.product.id,
                            brandName: tile.brand.name,
                            product: tile.product,
                            couponPromotionId: tile.couponPromotionId,
                          });
                        }}
                      >
                        {smallAddToBasketCtaText
                          ? t('Add')
                          : t('Add to basket')}
                      </StyledAddButton>
                    </ButtonWrapper>
                  }
                  soldoutCta={
                    <SoldOutButtonWrapper>
                      <StyledSoldOutButton
                        data-testid="soldOutRemindMeButton"
                        variant="secondary"
                        size="full-width"
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          onSoldoutCtaClick(tile.product.id);
                        }}
                      >
                        {t('Remind me')}
                      </StyledSoldOutButton>
                      <div
                        ref={
                          trackFeatureRef as React.RefObject<HTMLDivElement | null>
                        }
                      >
                        <StyledSoldOutButton
                          data-testid="similarProductsButtonSoldOut"
                          variant="secondary"
                          size="full-width"
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            onSimilarProductsClick(tile.product);
                          }}
                        >
                          {t('Similar Products')}
                        </StyledSoldOutButton>
                      </div>
                    </SoldOutButtonWrapper>
                  }
                  tileRef={ref as React.RefObject<HTMLAnchorElement | null>}
                  floatingHeaderElements={
                    <>
                      <WishlistCardHeaderActions $alignEnd={false}>
                        <RemoveItemButton
                          iconType={'trash'}
                          productId={tile.product.id}
                          onClickRemoveItem={handleClickRemoveItemButton}
                          onClickRemoveItemSuccess={
                            handleClickRemoveItemSuccess
                          }
                          onClickRemoveItemError={handleClickRemoveItemError}
                          onShowConfirm={
                            showConfirmOnDeleteItem
                              ? () => setIsConfirmModalVisible(true)
                              : undefined
                          }
                        />
                      </WishlistCardHeaderActions>
                    </>
                  }
                />
                {isSpinnerVisible && !showConfirmOnDeleteItem && (
                  <FullSizeSpinner
                    data-testid="WishlistTileFullSizeSpinner"
                    visible
                  >
                    <LoadingSpinner />
                  </FullSizeSpinner>
                )}
                <RemoveItemConfirmModal
                  isOpen={isConfirmModalVisible}
                  onClose={() => setIsConfirmModalVisible(false)}
                  onClickConfirm={handleClickRemoveItemButton}
                  onClickRemoveItemSuccess={handleClickRemoveItemSuccess}
                  onClickRemoveItemError={handleClickRemoveItemError}
                  isLoading={isSpinnerVisible}
                  productId={tile.product.id}
                />
              </>
            );
          }}
        />
      </ProductTileDeprecatedTracked>
    </>
  );
};

const StyledAddButton = styled(Button)`
  white-space: nowrap;
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin-top: 17px;
  gap: 12px;
  flex-flow: column;

  ${mobileOnly(css`
    margin-inline: 4px;
  `)}
`;

const SoldOutWrapper = styled.div`
  display: flex;
  gap: 12px;
  flex-flow: column;
  position: absolute;
  bottom: 136%;
  left: 20px;
  right: 20px;
`;

const StyledSoldOutButton = styled(Button)`
  white-space: nowrap;
  padding: 8px 40px;
`;

const WishlistCardHeaderActions = styled.div<{$alignEnd: boolean}>`
  z-index: ${Z_INDEXES.PRODUCT_TILE_CTA};
  flex: 1;
  display: flex;
  justify-content: ${props => (props.$alignEnd ? 'end' : 'space-between')};
  align-items: center;
  flex-direction: row-reverse;
`;

const FullSizeSpinner = styled.div<{visible: boolean}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: none;
  pointer-events: none;
  display: flex;
  justify-content: center;
  z-index: -1;
  opacity: 0;
  transition: opacity 300ms ease;

  ${({visible}) =>
    visible &&
    css`
      opacity: 1;
      background: rgba(255, 255, 255, 0.5);
      pointer-events: auto;
      z-index: 1;
    `};
`;
